import { create } from 'zustand';

/**
 * Canonical model + store for the Unified Activity Center.
 *
 * One Activity = one in-flight (or just-finished) unit of work, regardless of
 * where it came from: a server `/api/syncs` row, a client-driven SSE producer
 * (analysis / release / commit), a UI-kicked backfill, or a generic task. The
 * `progress` / `status` / `startedAt` fields deliberately mirror `SyncRun` so the
 * `eta.ts` helpers (progressFraction / etaLabel) work for both without casts.
 *
 * NOT persisted — live, in-memory only. Auto-dismiss is centralized here:
 * completed/stopped activities self-remove after ~10s; failed ones stay sticky
 * until the user dismisses them (so a failure is never missed).
 */

export type ActivitySource = 'sync' | 'analysis' | 'release' | 'commit' | 'backfill' | 'task';

// Reuses eta.ts STATUS_STYLE vocabulary (the running/terminal subset).
// 'partial' = finished but with failures (some work done) — terminal, amber,
// auto-dismissed like completed (not sticky like a hard failure).
export type ActivityStatus = 'running' | 'completed' | 'partial' | 'failed' | 'stopped';

export interface ActivityLogEntry {
  type: 'info' | 'success' | 'error';
  text: string;
  /** ISO timestamp; render via toLocale* with no hardcoded locale. */
  ts: string;
}

export interface Activity {
  id: string;
  source: ActivitySource;
  /** Reuses SyncRun.kind so eta.ts KIND_ICON / kindGroup apply. */
  kind: string;
  title: string;
  status: ActivityStatus;
  message?: string;
  /** SAME shape as SyncRun.progress (so eta.ts helpers apply structurally). */
  progress?: { phase?: string; done: number; total: number } | null;
  error?: string;
  logs?: ActivityLogEntry[];
  /** Live AI output, appended chunk-by-chunk. */
  streamingContent?: string;
  /** Whether this activity exposes a Stop action. */
  stoppable?: boolean;
  /** Links to a /api/syncs row → de-dups the running count by this id. */
  serverRunId?: string;
  href?: { label: string; to: string };
  startedAt: string;
  finishedAt?: string;
}

/** How long completed/stopped activities linger before self-removing (ms). */
export const AUTO_DISMISS_MS = 10000;

/** Cap on retained log lines per activity (older lines are dropped). */
export const MAX_LOG_LINES = 200;

const TERMINAL: ReadonlySet<ActivityStatus> = new Set<ActivityStatus>(['completed', 'partial', 'failed', 'stopped']);

interface ActivityState {
  activities: Activity[];
  panelOpen: boolean;

  /** Insert or merge by id (preserving logs/stream unless explicitly replaced). */
  upsert: (activity: Activity) => void;
  /** Patch fields on an existing activity by id (no-op if missing). */
  update: (id: string, patch: Partial<Activity>) => void;
  /** Append one log line (auto-stamped ts), capped to MAX_LOG_LINES. */
  appendLog: (id: string, entry: Omit<ActivityLogEntry, 'ts'> & { ts?: string }) => void;
  /** Append AI streaming output. */
  appendStream: (id: string, chunk: string) => void;
  /** Reset the streaming buffer (e.g. between items in a batch). */
  clearStream: (id: string) => void;
  /** Mark an activity completed (and schedule its auto-dismiss). */
  complete: (id: string, patch?: Partial<Activity>) => void;
  /** Mark an activity failed (sticky — no auto-dismiss). */
  fail: (id: string, error?: string, patch?: Partial<Activity>) => void;
  /** Remove an activity outright. */
  remove: (id: string) => void;
  /** Remove every non-running activity. */
  clearFinished: () => void;
  /** Toggle the Activity Center tray. */
  togglePanel: () => void;
  setPanelOpen: (open: boolean) => void;
}

/** Centralized auto-dismiss: a completed/stopped activity self-removes after
 *  AUTO_DISMISS_MS, but only if it's still in that exact terminal state when the
 *  timer fires (a later re-run that flips it back to running cancels removal). */
function scheduleAutoDismiss(id: string, status: ActivityStatus) {
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    const cur = useActivityStore.getState().activities.find((a) => a.id === id);
    if (cur && cur.status === status && TERMINAL.has(status) && status !== 'failed') {
      useActivityStore.getState().remove(id);
    }
  }, AUTO_DISMISS_MS);
}

function capLogs(logs: ActivityLogEntry[]): ActivityLogEntry[] {
  return logs.length > MAX_LOG_LINES ? logs.slice(logs.length - MAX_LOG_LINES) : logs;
}

export const useActivityStore = create<ActivityState>()((set, get) => ({
  activities: [],
  panelOpen: false,

  upsert: (activity) => {
    set((state) => {
      const idx = state.activities.findIndex((a) => a.id === activity.id);
      if (idx === -1) return { activities: [...state.activities, activity] };
      const merged = { ...state.activities[idx], ...activity };
      const next = state.activities.slice();
      next[idx] = merged;
      return { activities: next };
    });
    // An upsert that lands a non-failed terminal state schedules its dismissal.
    if (activity.status !== 'running' && activity.status !== 'failed') {
      scheduleAutoDismiss(activity.id, activity.status);
    }
  },

  update: (id, patch) =>
    set((state) => ({
      activities: state.activities.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    })),

  appendLog: (id, entry) =>
    set((state) => ({
      activities: state.activities.map((a) =>
        a.id === id
          ? { ...a, logs: capLogs([...(a.logs ?? []), { ...entry, ts: entry.ts ?? new Date().toISOString() }]) }
          : a,
      ),
    })),

  appendStream: (id, chunk) =>
    set((state) => ({
      activities: state.activities.map((a) =>
        a.id === id ? { ...a, streamingContent: (a.streamingContent ?? '') + chunk } : a,
      ),
    })),

  clearStream: (id) =>
    set((state) => ({
      activities: state.activities.map((a) => (a.id === id ? { ...a, streamingContent: '' } : a)),
    })),

  complete: (id, patch) => {
    if (!get().activities.some((a) => a.id === id)) return;
    set((state) => ({
      activities: state.activities.map((a) =>
        a.id === id
          ? { ...a, status: 'completed' as const, finishedAt: a.finishedAt ?? new Date().toISOString(), ...patch }
          : a,
      ),
    }));
    scheduleAutoDismiss(id, 'completed');
  },

  fail: (id, error, patch) => {
    if (!get().activities.some((a) => a.id === id)) return;
    // No auto-dismiss — failures are sticky until the user dismisses them.
    set((state) => ({
      activities: state.activities.map((a) =>
        a.id === id
          ? { ...a, status: 'failed' as const, error: error ?? a.error, finishedAt: a.finishedAt ?? new Date().toISOString(), ...patch }
          : a,
      ),
    }));
  },

  remove: (id) => set((state) => ({ activities: state.activities.filter((a) => a.id !== id) })),

  clearFinished: () => set((state) => ({ activities: state.activities.filter((a) => a.status === 'running') })),

  togglePanel: () => set((state) => ({ panelOpen: !state.panelOpen })),
  setPanelOpen: (panelOpen) => set({ panelOpen }),
}));

// DEV-only: expose the store on window for local QA/repro of the Activity Center.
// Tree-shaken from prod (import.meta.env.DEV).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __activityStore?: typeof useActivityStore }).__activityStore = useActivityStore;
}

// ── Selectors (the indicator's single source of truth) ─────────────────────

/** Count of distinct RUNNING activities, de-duped by serverRunId so a
 *  client-started run that also surfaces as a server sync row counts once.
 *  Activities without a serverRunId each count individually. */
export function selectActiveCount(state: ActivityState): number {
  const seen = new Set<string>();
  let count = 0;
  for (const a of state.activities) {
    if (a.status !== 'running') continue;
    if (a.serverRunId) {
      if (seen.has(a.serverRunId)) continue;
      seen.add(a.serverRunId);
    }
    count += 1;
  }
  return count;
}

/** True while ANY failed activity remains (red until each is dismissed). */
export function selectHasError(state: ActivityState): boolean {
  return state.activities.some((a) => a.status === 'failed');
}

/** The ordered, de-duped list the Activity Center renders (NOT the raw
 *  `activities` array). RUNNING activities come first (oldest-started first, so
 *  the list is stable as new runs arrive); then terminal ones
 *  (completed/stopped/failed) newest-finished first. De-duped by serverRunId —
 *  if both a running and a terminal row share a serverRunId, the running one
 *  wins (and the terminal duplicate is dropped). Activities without a
 *  serverRunId are always kept individually. */
// Takes the raw `activities` array (NOT the store state) so callers can wrap it in
// useMemo — returning a fresh array straight from a zustand selector would change
// the snapshot reference every render and trigger an infinite re-render loop.
export function selectVisibleActivities(activities: Activity[]): Activity[] {
  // First pass: pick a winner per serverRunId (running beats terminal).
  const byRunId = new Map<string, Activity>();
  const kept: Activity[] = [];
  for (const a of activities) {
    if (!a.serverRunId) {
      kept.push(a);
      continue;
    }
    const existing = byRunId.get(a.serverRunId);
    if (!existing) {
      byRunId.set(a.serverRunId, a);
      kept.push(a);
      continue;
    }
    // A running entry always wins over a terminal duplicate.
    if (a.status === 'running' && existing.status !== 'running') {
      const idx = kept.indexOf(existing);
      if (idx !== -1) kept[idx] = a;
      byRunId.set(a.serverRunId, a);
    }
  }

  const running = kept.filter((a) => a.status === 'running');
  const terminal = kept.filter((a) => a.status !== 'running');

  // Running: oldest-started first (ascending startedAt) for a stable list.
  running.sort((x, y) => x.startedAt.localeCompare(y.startedAt));
  // Terminal: newest-finished first (descending finishedAt; missing = oldest).
  terminal.sort((x, y) => (y.finishedAt ?? '').localeCompare(x.finishedAt ?? ''));

  return [...running, ...terminal];
}
